import { useCallback, useEffect, useRef } from 'react';

// Cloudflare Turnstile, explicit rendering in "execute" mode: the check runs
// only when getToken() is called (right before a code is sent). Most visitors
// never see it; a suspicious one gets a checkbox inside the container.

interface TurnstileApi {
  render: (container: HTMLElement, options: Record<string, unknown>) => string;
  execute: (widgetId: string) => void;
  reset: (widgetId: string) => void;
  remove: (widgetId: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

const SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
const FAILED_MESSAGE = 'Verifikasi keamanan gagal. Silakan coba lagi.';

let scriptPromise: Promise<TurnstileApi> | null = null;

const loadTurnstile = (): Promise<TurnstileApi> => {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  scriptPromise ??= new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = SCRIPT_URL;
    script.async = true;
    script.onload = () =>
      window.turnstile ? resolve(window.turnstile) : reject(new Error(FAILED_MESSAGE));
    script.onerror = () => {
      scriptPromise = null;
      script.remove();
      reject(new Error('Verifikasi keamanan tidak dapat dimuat. Periksa koneksi Anda.'));
    };
    document.head.appendChild(script);
  });
  return scriptPromise;
};

interface Pending {
  resolve: (token: string) => void;
  reject: (err: Error) => void;
}

/** Render an element with `containerRef`; `getToken()` resolves with a fresh, single-use token. */
const useTurnstile = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const widget = useRef<Promise<{ api: TurnstileApi; id: string }> | null>(null);
  const pending = useRef<Pending | null>(null);

  useEffect(() => {
    const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY;
    const container = containerRef.current;
    if (!container) return;
    const settle = (fn: (p: Pending) => void) => {
      if (pending.current) fn(pending.current);
      pending.current = null;
    };
    const ready = siteKey
      ? loadTurnstile().then((api) => {
          const id = api.render(container, {
            sitekey: siteKey,
            execution: 'execute',
            appearance: 'interaction-only',
            language: 'id',
            callback: (token: string) => settle((p) => p.resolve(token)),
            'error-callback': () => settle((p) => p.reject(new Error(FAILED_MESSAGE))),
            'timeout-callback': () => settle((p) => p.reject(new Error(FAILED_MESSAGE))),
          });
          return { api, id };
        })
      : Promise.reject(new Error('Verifikasi keamanan belum dikonfigurasi.'));
    ready.catch(() => {});
    widget.current = ready;
    return () => {
      ready.then(({ api, id }) => api.remove(id)).catch(() => {});
      widget.current = null;
    };
  }, []);

  const getToken = useCallback(async (): Promise<string> => {
    if (!widget.current) throw new Error(FAILED_MESSAGE);
    const { api, id } = await widget.current;
    return new Promise<string>((resolve, reject) => {
      pending.current?.reject(new Error(FAILED_MESSAGE));
      pending.current = { resolve, reject };
      // A token is single-use: start every check from a fresh widget state
      api.reset(id);
      api.execute(id);
    });
  }, []);

  return { containerRef, getToken };
};

export default useTurnstile;
